/**
 * Helpers for grouping custom member fields into sections on the
 * Add/Edit Member form.
 *
 * A custom field's section is driven by `com_fields.category` (a string).
 * Fields with a blank category (null, '', or whitespace-only) are NOT a real
 * section — they must render only under the "Additional Information" bucket.
 * If a blank category is treated as its own section it produces an *untitled*
 * card that duplicates those same fields (TRUS-236), because the Additional
 * Information bucket already matches every field whose category is falsy.
 */

/** True when `category` is a real, named section (not blank/whitespace-only). */
export function isNamedCategory(category: string | null | undefined): boolean {
  return typeof category === 'string' && category.trim().length > 0;
}

/** Minimal slice of a core field's profile config needed to decide requiredness. */
export interface CoreFieldPresence {
  field_key: string;
  is_core_field: boolean;
  is_hidden: boolean;
  is_required?: boolean;
}

/** A core field the Add/Edit Member form can render. */
export interface RequiredCandidate {
  /** Name on the form / MemberFormData. */
  field: string;
  /**
   * Core-field REGISTRY key, which is NOT always the form-field name:
   * `dob` → `date_of_birth`. Getting this
   * wrong makes the lookup silently miss and the field fall back to whatever
   * the no-config branch does.
   */
  registryKey: string;
  label: string;
  /**
   * Required for legacy-layout orgs, which have no per-org config to read.
   * Ignored by configured orgs, where `is_required` decides.
   */
  legacyRequired?: boolean;
}

/**
 * Which core fields the Add/Edit Member form requires, as {formField: label}.
 *
 * For a configured org this is simply what the org's field configuration says:
 * `is_required` on a field that is present and not hidden. That config is the
 * per-org source of truth — it is what the Profile Builder edits and what the
 * client's field spec is transcribed into — so the form must read it rather than
 * carry its own opinion.
 *
 * It previously carried its own opinion, and drifted from the spec twice over:
 *
 *   * `gender` was required because it was in a hardcoded baseline list, though
 *     Somos' config (and their spec) marks it optional; and
 *   * `zip_code` was NOT required, because it was absent from that list, though
 *     Somos' config (and their spec) marks it required.
 *
 * Earlier still, the list hardcoded `email` as required while Somos hides it,
 * which blocked member creation outright — the same defect the backend
 * `MemberCreate` contract carried and had fixed separately.
 *
 * A hidden field is never required regardless of `is_required`: the form does
 * not render it, so demanding it is a dead end the user cannot resolve.
 *
 * Legacy-layout orgs have no config to consult and keep the previous baseline
 * behavior via `legacyRequired`, which bounds the change to configured orgs.
 *
 * The result drives BOTH submit validation and the required markers on labels.
 * Those were independent before, so a label could star a field the form would
 * save without — and did.
 */
export function computeRequiredCoreFields(
  profileFields: CoreFieldPresence[],
  useConfigured: boolean,
  candidates: RequiredCandidate[],
): Record<string, string> {
  const configFor = (registryKey: string) =>
    profileFields.find((f) => f.is_core_field && f.field_key === registryKey);

  const out: Record<string, string> = {};
  for (const { field, registryKey, label, legacyRequired } of candidates) {
    const config = configFor(registryKey);

    if (useConfigured) {
      // Present, visible, and marked required by the org.
      if (config && !config.is_hidden && config.is_required === true) out[field] = label;
      continue;
    }

    // Legacy layout: the old baseline set, minus anything explicitly hidden.
    if (legacyRequired && !(config?.is_hidden ?? false)) out[field] = label;
  }
  return out;
}

/**
 * Normalize a category token so the two naming conventions in use compare equal.
 *
 * A program's "show only these field categories" allowlist
 * (`program_custom_field_categories.category`) is free text, and orgs have
 * populated it with BOTH conventions — a category's display name
 * ("Completed Activities") and its key ("hub_meeting") — sometimes in the same
 * program. Somos' Promotora Activity Tracking lists all of
 * `Demographic, Status, outreach, Completed Activities, Activity Tracking,
 * hub_meeting, training_workshop`.
 *
 * Comparing that raw string against the display name alone made section
 * visibility depend on a label an admin can rename at will: renaming the
 * "Status" section to "Engagement" silently dropped it (and every field in it)
 * from the Add/Edit Member form, with no error and nothing in the UI to say
 * why. Normalizing lets an allowlist entry match a category by EITHER its key
 * or its display name.
 */
export function normalizeCategoryToken(token: string | null | undefined): string {
  if (typeof token !== 'string') return '';
  return token.trim().toLowerCase().replace(/[\s_-]+/g, '_');
}

/**
 * True when a program allowlist permits this category.
 *
 * `allowed` is the raw allowlist. A category matches if either its key or its
 * display name normalizes to an allowed token. A null/empty allowlist means
 * "no restriction" — every category is allowed.
 */
export function isCategoryAllowed(
  allowed: string[] | null | undefined,
  category: { key?: string | null; name?: string | null },
): boolean {
  if (!allowed || allowed.length === 0) return true;
  const tokens = new Set(allowed.map(normalizeCategoryToken));
  tokens.delete('');
  if (tokens.size === 0) return true;
  const key = normalizeCategoryToken(category.key);
  const name = normalizeCategoryToken(category.name);
  return (!!key && tokens.has(key)) || (!!name && tokens.has(name));
}

/**
 * Minimal shape of a profile-field config needed to build the ordered,
 * grouped Demographics layout. Mirrors the relevant slice of
 * `ProfileField` (services/api/members) so this pure helper stays testable
 * without pulling in the whole API type surface.
 */
export interface ConfiguredGroupField {
  field_key: string;
  category_key: string;
  category_name?: string;
  category_sort?: number;
  is_core_field: boolean;
  is_hidden: boolean;
  is_required: boolean;
  field_sort?: number;
}

export interface ConfiguredGroup<T extends ConfiguredGroupField = ConfiguredGroupField> {
  key: string;
  name: string;
  sort: number;
  items: T[];
}

/**
 * Build the ordered, deduped section groups the Add/Edit Member form and the
 * Member Profile both render for orgs that use the Profile Builder. This is a
 * pure extraction of MemberProfileNew.renderConfiguredFields()'s grouping so
 * the Add form mirrors the Profile's structure/order exactly (TRUS-290):
 *
 * - skip hidden fields;
 * - skip the family_member / household categories (own tabs);
 * - dedupe by field_key — render each field once, in the FIRST category it
 *   appears under (a field can appear under both its configured category and a
 *   com_fields.category text match);
 * - groups ordered by category_sort (?? 999);
 * - within a group: required fields first, then field_sort (?? 0).
 */
export function buildConfiguredGroups<T extends ConfiguredGroupField>(
  profileFields: T[],
): Array<ConfiguredGroup<T>> {
  const groups: Array<ConfiguredGroup<T>> = [];
  const byKey = new Map<string, ConfiguredGroup<T>>();
  const seenKeys = new Set<string>();

  for (const pf of profileFields) {
    if (pf.is_hidden) continue;
    if (pf.category_key === 'family_member' || pf.category_key === 'household') continue;
    if (seenKeys.has(pf.field_key)) continue;
    seenKeys.add(pf.field_key);

    let g = byKey.get(pf.category_key);
    if (!g) {
      g = {
        key: pf.category_key,
        name: pf.category_name || pf.category_key,
        sort: pf.category_sort ?? 999,
        items: [],
      };
      byKey.set(pf.category_key, g);
      groups.push(g);
    }
    g.items.push(pf);
  }

  groups.sort((a, b) => a.sort - b.sort);
  for (const g of groups) {
    g.items.sort((a, b) => {
      const ra = a.is_required ? 0 : 1;
      const rb = b.is_required ? 0 : 1;
      if (ra !== rb) return ra - rb;
      return (a.field_sort ?? 0) - (b.field_sort ?? 0);
    });
  }

  return groups;
}
